import { type MachineAttachmentDto, MachinesApiService } from '@api/machines'
import { handleCatchError } from '@common/utils'
import {
  MolsonButton,
  MolsonDialog,
  MolsonDialogActions,
  MolsonDialogContent,
  MolsonDialogHeader
} from '@components/reusable'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type QuoteItemData, QuoteItemsGrid } from './QuoteItemsGrid'

export type QuoteItemsDialogProps = {
  closeDialog: () => void
  isDialogOpen: boolean
  quoteId: number | null
  dialogKeyPrefix: string
  gridKeyPrefix: string
}

export const QuoteItemsDialog = ({
  closeDialog,
  isDialogOpen,
  quoteId,
  dialogKeyPrefix,
  gridKeyPrefix
}: QuoteItemsDialogProps) => {
  const { t } = useTranslation('', {
    keyPrefix: dialogKeyPrefix
  })

  const [quoteItems, setQuoteItems] = useState<QuoteItemData[]>([])
  const [quoteName, setQuoteName] = useState<string>('')

  // TODO: replace with React Query
  const fetchQuote = useCallback(async () => {
    if (quoteId) {
      try {
        const { quotation } = await MachinesApiService.quotation(
          quoteId.toString()
        )

        const nestedAttachmentsList = quotation?.machineItems?.flatMap(
          machine => machine.machineAttachments
        ) as MachineAttachmentDto[]

        const machines = quotation?.machineItems?.map(machine => ({
          brand: machine.brand,
          category: machine.category,
          modelName: machine.modelName,
          manufactureYear: machine.manufactureYear,
          stockType: machine.stockType
        })) as QuoteItemData[]
        const nestedAttachments = nestedAttachmentsList?.map(attachment => ({
          brand: attachment.brand,
          category: attachment.category,
          modelName: attachment.modelName,
          manufactureYear: attachment.manufactureYear,
          stockType: attachment.stockType
        })) as QuoteItemData[]
        const attachments = quotation?.machineAttachmentItems?.map(
          attachment => ({
            brand: attachment.brand,
            category: attachment.category,
            modelName: attachment.modelName,
            manufactureYear: attachment.manufactureYear,
            stockType: attachment.stockType
          })
        ) as QuoteItemData[]

        const quoteItems = [machines, nestedAttachments, attachments].flat()
        setQuoteItems(quoteItems)
        setQuoteName(quotation?.quotationName ?? '')
      } catch (err) {
        handleCatchError(err)
      }
    }
  }, [quoteId])

  useEffect(() => {
    fetchQuote()
  }, [fetchQuote])

  return (
    <MolsonDialog
      fullWidth={true}
      maxWidth="lg"
      onClose={closeDialog}
      open={isDialogOpen && quoteItems.length !== 0}
    >
      <MolsonDialogHeader
        subTitle={`${t('showAllItemsInQuote', {
          quoteName,
          quoteId
        })}`}
        title={`${t('quoteItems', {
          quoteId
        })}`}
      />

      {quoteItems.length > 0 && (
        <MolsonDialogContent>
          <QuoteItemsGrid data={quoteItems} keyPrefix={gridKeyPrefix} />
        </MolsonDialogContent>
      )}

      <MolsonDialogActions>
        <MolsonButton onClick={closeDialog} variant="contained">
          {t('backToMyQuotes')}
        </MolsonButton>
      </MolsonDialogActions>
    </MolsonDialog>
  )
}
